import { useCallback, useEffect, useRef, useState } from "react"
import { useDispatch } from "react-redux"
import { enqueueSnackbar } from "notistack"
import { AppDispatch } from "@core/store"
import { getApiErrorMessage } from "@/core/utils"
import { dayKeyFromApi } from "../model/mskCalendar"
import { Period, buildScheduleListArgs } from "../model/period"
import { CellKey, ScheduleMap, cellKey } from "../model/scheduleCells"
import {
  staffScheduleApi,
  useCreateSchedulesMutation,
  useDeleteSchedulesMutation,
} from "../store"
import { Brush } from "./useBrush"

// Временные id оптимистичных записей: монотонный счётчик уровня модуля,
// отрицательные, без переиспользования. Переиспользование дало бы коллизию ключей
// React при двух мазках подряд по одной ячейке.
let tempIdSeq = -1
const nextTempId = () => tempIdSeq--

export interface StrokeTarget {
  staffId: number
  fromIdx: number
  toIdx: number
}

export interface UsePaintCommitResult {
  commit: (stroke: StrokeTarget) => void
  /** Ячейки «в полёте» — новый Set при каждом изменении (для React.memo строк). */
  pendingKeys: ReadonlySet<CellKey>
  /** Очередь не пуста — кнопка «Обновить» заблокирована. */
  isBusy: boolean
}

/**
 * Оптимистичные коммиты мазков. Вызывается ОДИН РАЗ, на уровне schedulePage:
 * второй инстанс создал бы вторую очередь и второе множество pendingKeys.
 *
 * Мутации расписания намеренно не используют ни invalidatesTags, ни onQueryStarted —
 * оптимизмом владеет этот хук (спека, раздел 6).
 */
export const usePaintCommit = (
  period: Period,
  cells: ScheduleMap,
  brush: Brush,
  refetchSchedules: () => void,
): UsePaintCommitResult => {
  const dispatch = useDispatch<AppDispatch>()
  const [createSchedules] = useCreateSchedulesMutation()
  const [deleteSchedules] = useDeleteSchedulesMutation()

  // cells и refetch — в ref: идентичность commit не должна меняться на каждый патч,
  // иначе обработчики грида пересоздавались бы на каждое движение кисти. Тело запроса
  // вычисляется в момент постановки в очередь по актуальной (уже пропатченной) карте.
  const cellsRef = useRef(cells)
  useEffect(() => {
    cellsRef.current = cells
  }, [cells])
  const refetchRef = useRef(refetchSchedules)
  useEffect(() => {
    refetchRef.current = refetchSchedules
  }, [refetchSchedules])

  const queueRef = useRef<Promise<void>>(Promise.resolve())
  const inFlightRef = useRef(0)
  const [isBusy, setIsBusy] = useState(false)

  const pendingRef = useRef<Set<CellKey>>(new Set())
  const [pendingKeys, setPendingKeys] = useState<ReadonlySet<CellKey>>(
    new Set(),
  )
  const mutatePending = useCallback((mutate: (next: Set<CellKey>) => void) => {
    const next = new Set(pendingRef.current)
    mutate(next)
    pendingRef.current = next
    setPendingKeys(next)
  }, [])

  const enqueueCommit = useCallback(
    (
      keys: CellKey[],
      patchResult: { undo: () => void },
      run: () => Promise<void>,
    ) => {
      mutatePending((next) => keys.forEach((k) => next.add(k)))
      inFlightRef.current += 1
      setIsBusy(true)
      const task = async () => {
        try {
          await run()
        } catch (e) {
          // Исход мутации сюда попадает ТОЛЬКО через .unwrap() в run — промис триггера
          // RTK при ошибке не реджектится, а разрешается { error }.
          try {
            patchResult.undo()
          } catch (undoError) {
            // Если параллельный перезапрос уже заменил карту и путь inverse-патча исчез,
            // Immer бросает ошибку. Авторитетным состоянием всё равно станет refetch ниже,
            // поэтому мазок не считается потерянным — но факт нужно видеть в консоли.
            console.warn(
              `staffSchedule: не удалось откатить оптимистичный патч (${keys.join(", ")})`,
              undoError,
            )
          }
          // Откат восстанавливает состояние ДО мазка, а не состояние сервера —
          // без перезапроса на экране могла бы остаться ячейка-призрак (спека, раздел 12).
          refetchRef.current()
          enqueueSnackbar(getApiErrorMessage(e), { variant: "error" })
        } finally {
          inFlightRef.current -= 1
          mutatePending((next) => keys.forEach((k) => next.delete(k)))
          if (inFlightRef.current === 0) setIsBusy(false)
        }
      }
      // Цепочка продвигается независимо от исхода предыдущего коммита —
      // иначе первая ошибка навсегда заклинила бы очередь.
      queueRef.current = queueRef.current.then(task, task)
    },
    [mutatePending],
  )

  const commit = useCallback(
    (stroke: StrokeTarget) => {
      if (!brush) return
      const currentCells = cellsRef.current
      const listArgs = buildScheduleListArgs(period)
      const daysInStroke = period.days.slice(stroke.fromIdx, stroke.toIdx + 1)

      if (brush.kind === "shift") {
        const shiftTypeId = brush.shiftTypeId
        const targets: { key: CellKey; dayKey: string }[] = []
        for (const day of daysInStroke) {
          const key = cellKey(stroke.staffId, day.key)
          if (pendingRef.current.has(key)) continue // ячейка в полёте
          if (currentCells[key]?.shift_type_id === shiftTypeId) continue // уже этот тип
          targets.push({ key, dayKey: day.key })
        }
        if (targets.length === 0) return

        const patchResult = dispatch(
          staffScheduleApi.util.updateQueryData(
            "schedulesList",
            listArgs,
            (draft) => {
              for (const { key, dayKey } of targets) {
                const existing = draft[key]
                if (existing) {
                  existing.shift_type_id = shiftTypeId // перекраска: id сохраняется
                } else {
                  draft[key] = {
                    id: nextTempId(),
                    staff_id: stroke.staffId,
                    shift_type_id: shiftTypeId,
                    date: `${dayKey}T00:00:00+03:00`,
                  }
                }
              }
            },
          ),
        )
        enqueueCommit(
          targets.map((t) => t.key),
          patchResult,
          async () => {
            const res = await createSchedules({
              staff_id: stroke.staffId,
              shift_id: shiftTypeId, // ловушка контракта: в теле shift_id, не shift_type_id
              dates: targets.map((t) => t.dayKey),
            }).unwrap()
            // Сверка по ответу — upsert по ключу, а не замена: к моменту ответа refetch
            // мог уже убрать временную запись, и слепая замена восстановила бы её.
            // date в ответе — ISO, ключ строится через dayKeyFromApi (граница системы).
            dispatch(
              staffScheduleApi.util.updateQueryData(
                "schedulesList",
                listArgs,
                (draft) => {
                  for (const s of res.schedules ?? []) {
                    draft[cellKey(s.staff_id, dayKeyFromApi(s.date))] = s
                  }
                },
              ),
            )
          },
        )
      } else {
        // Ластик: один мазок = один DELETE /delete с телом { ids } — частичного отказа не бывает.
        const keys: CellKey[] = []
        const ids: number[] = []
        for (const day of daysInStroke) {
          const key = cellKey(stroke.staffId, day.key)
          if (pendingRef.current.has(key)) continue // в полёте — id временный, удалять нечего
          const existing = currentCells[key]
          if (!existing) continue
          keys.push(key)
          ids.push(existing.id)
        }
        if (keys.length === 0) return

        const patchResult = dispatch(
          staffScheduleApi.util.updateQueryData(
            "schedulesList",
            listArgs,
            (draft) => {
              for (const key of keys) {
                delete draft[key]
              }
            },
          ),
        )
        enqueueCommit(keys, patchResult, async () => {
          await deleteSchedules({ ids }).unwrap()
        })
      }
    },
    [brush, period, dispatch, createSchedules, deleteSchedules, enqueueCommit],
  )

  return { commit, pendingKeys, isBusy }
}
